/* The questions the sales team answers on the phone every day.
 *
 * This list is the fallback, not the source. Once the showroom has filed its
 * own questions in the console — Storefront → Questions — those are what the
 * site shows, and these are what it falls back to while that list is empty
 * or the API cannot be reached. See hooks/useFaqs.
 *
 * An answer here is plain text. The answers that come from the console are
 * short HTML — bold, links, a list — which is why both are rendered through
 * the same `rich-text` block in the accordion. */

export type Faq = { id: string; question: string; answer: string }

export const FAQS: Faq[] = [
  {
    id: 'how-financing-works',
    question: 'How does vehicle financing work?',
    answer:
      'You choose the car and we prepare a single application, then put it in front of every partner bank at once. Each returns a markup rate and a monthly instalment. You pick the offer you want, sign with that bank, and we handle the paperwork through to delivery.',
  },
  {
    id: 'which-banks',
    question: 'Which banks do you work with?',
    answer:
      'Askari, Faysal, Standard Chartered, UBL, Bank AL Habib, Bank Alfalah, JS and BOP — eight of them, Islamic and conventional both. Tell us which structure you want and the application goes to the banks that write it.',
  },
  {
    id: 'documents',
    question: 'What documents are required?',
    answer:
      'CNIC copy, two photographs, and proof of income: salary slips and six months of bank statements if you are salaried, or tax returns and business proof if you are self-employed. Some banks also ask for a utility bill as address proof.',
  },
  {
    id: 'down-payment',
    question: 'How much down payment is required?',
    answer:
      'Most banks start at 20 to 30 per cent of the vehicle price. A larger down payment lowers both the monthly instalment and the total markup you pay across the term, so it is worth modelling both in the calculator.',
  },
  {
    id: 'approval-time',
    question: 'How long does financing approval take?',
    answer:
      'Typically 48 hours once your documents are complete. A few banks return a decision the same day. Incomplete income proof is the single most common cause of delay.',
  },
  {
    id: 'apply-online',
    question: 'Can I apply online?',
    answer:
      'Yes. Send us the car and your details through the site or WhatsApp and we start the file the same day. You only need to visit the showroom to sign and collect.',
  },
  {
    id: 'test-drive',
    question: 'Can I book a test drive?',
    answer:
      'Yes, seven days a week between 10:00 and 20:00. Book from any vehicle page or message us, and we will have the car ready when you arrive.',
  },
  {
    id: 'all-financeable',
    question: 'Are all vehicles available for financing?',
    answer:
      'Nearly all. Cars imported to order are the exception — banks want a bespoke file for those, so they are marked as not available on lease rather than quietly hidden.',
  },
  {
    id: 'booking-confirmation',
    question: 'How do I receive my booking confirmation?',
    answer:
      'Once your payment is processed, the confirmation and your receipt are sent to the email address on your account within a few minutes. You can also open My bookings on this site at any time to see the booking and download the receipt yourself.',
  },
  {
    id: 'change-or-cancel',
    question: 'Can I make changes or cancel my booking online?',
    answer:
      'Yes. Request a change or a cancellation from the My bookings page, or tell the person handling your file. Changes made before the bank disburses cost nothing; after that they are subject to the bank’s own rules and any dealership charge, and we will tell you the figure before anything is signed.',
  },
  {
    id: 'no-confirmation-email',
    question: 'What should I do if I don’t receive my confirmation email?',
    answer:
      'Check your spam and promotions folders first. If it is not there, sign in and open My bookings — the booking is on your account whether the email arrived or not. Or message us with your name and the car, and we will resend it straight away.',
  },
  {
    id: 'hidden-fees',
    question: 'Are there any hidden fees or extra charges at checkout?',
    answer:
      'None. Insurance, tracker, registration, withholding tax and the bank’s processing fee are itemised in your quote before you sign, and the monthly figure you were shown is the one on the contract. Nothing appears at signing that was not on that quote.',
  },
]
